/**
 * Página de Logs de Auditoria e Ocorrências
 * Dashboard administrativo para monitorar ações sensíveis e moderar denúncias.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Sidebar } from '@/components/common/Sidebar';
import { ThemeToggle } from '@/components/common';
import {
  getDefaultRouteByRole,
  isAdminRole,
  shouldFetchAdminResources,
} from '@/lib/roleRouting';
import {
  Search,
  Loader2,
  AlertCircle,
  ShieldCheck,
  History,
  AlertTriangle,
  Filter,
  Calendar,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  MessageSquare,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  getAuditLogs,
  processOccurrence,
  AuditLogDTO,
} from '@/services/admins';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { GlobalSearch, NotificationCenter } from '@/components/dashboard/HeaderActions';

const ITEMS_PER_PAGE = 10;

export default function AuditLogsPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();
  
  const [activeTab, setActiveTab] = useState<'HISTORICAL' | 'OCCURRENCE'>('OCCURRENCE');
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [logs, setLogs] = useState<AuditLogDTO[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [selectedLog, setSelectedLog] = useState<AuditLogDTO | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');

  // Check auth
  useEffect(() => {
    if (!isLoading) {
      if (!user) {
        navigate('/login');
        return;
      }
      if (!isAdminRole(user.role)) {
        navigate(getDefaultRouteByRole(user.role), { replace: true });
      }
    }
  }, [user, isLoading, navigate]);

  // Load logs
  const loadLogs = async () => {
    setIsLoadingData(true);
    try {
      const params: any = {
        category: activeTab,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };

      if (statusFilter !== 'ALL') {
        params.status = statusFilter;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (startDate) {
        params.startDate = new Date(startDate).toISOString();
      }

      if (endDate) {
        // Set end of day
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        params.endDate = end.toISOString();
      }
      
      const response = await getAuditLogs(params);
      if (response.success && response.data) {
        setLogs(response.data.items);
        setTotalItems(response.data.total);
      }
    } catch (err) {
      console.error('Erro ao carregar logs:', err);
      toast.error('Não foi possível carregar os logs de auditoria.');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleClearDateFilter = () => {
    setStartDate('');
    setEndDate('');
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldFetchAdminResources(user?.role, isLoading)) {
        loadLogs();
      }
    }, searchTerm ? 500 : 0); // Debounce search
    
    return () => clearTimeout(timer);
  }, [user, isLoading, activeTab, currentPage, statusFilter, searchTerm, startDate, endDate]);

  const handleProcess = async (status: 'RESOLVED' | 'REJECTED' | 'ARCHIVED') => {
    if (!selectedLog) return;
    
    setIsProcessing(true);
    try {
      const response = await processOccurrence(selectedLog.id, {
        status,
        resolutionNote: resolutionNote,
      });

      if (response.success) {
        toast.success(`Ocorrência ${status.toLowerCase()} com sucesso.`);
        setIsDetailsOpen(false);
        setResolutionNote('');
        loadLogs();
      } else {
        toast.error(response.error || 'Erro ao processar ocorrência.');
      }
    } catch (err) {
      toast.error('Erro ao conectar com o servidor.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'PENDING':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pendente</Badge>;
      case 'IN_ANALYSIS':
        return <Badge className="bg-blue-100 text-blue-800 border-blue-200">Em análise</Badge>;
      case 'RESOLVED':
        return <Badge className="bg-green-100 text-green-800 border-green-200">Resolvido</Badge>;
      case 'REJECTED':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Rejeitado</Badge>;
      case 'ARCHIVED':
        return <Badge className="bg-gray-100 text-gray-800 border-gray-200">Arquivado</Badge>;
      default:
        return null;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'DOCTOR_APPROVAL': return 'Aprovação de Médico';
      case 'DOCTOR_REJECTION': return 'Recusa de Médico';
      case 'USER_SUSPENSION': return 'Suspensão de Usuário';
      case 'USER_REACTIVATION': return 'Reativação de Usuário';
      case 'USER_REPORT': return 'Denúncia';
      default: return type;
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  if (!isAdminRole(user.role)) {
    return null;
  }

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar 
        userRole={user.role}
        userName={user.name || ''} 
        onLogout={async () => {
          await logout();
          navigate('/login');
        }} 
      />

      <div className="flex-1 flex flex-col">
        <header className="bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 px-8 py-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShieldCheck className="w-8 h-8 text-teal-600" />
              Logs de Auditoria e Ocorrências
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Rastreabilidade de ações administrativas e moderação de denúncias.
            </p>
          </div>

          <div className="flex items-center gap-4">
            <GlobalSearch />
            <NotificationCenter />
            <ThemeToggle />
          </div>
        </header>

        <main className="flex-1 p-8 overflow-auto">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-neutral-800 mb-6">
            <button
              onClick={() => { setActiveTab('OCCURRENCE'); setCurrentPage(1); }}
              className={`px-6 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'OCCURRENCE'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <AlertTriangle className="w-4 h-4" />
              Ocorrências Tratáveis
              {totalItems > 0 && activeTab === 'OCCURRENCE' && (
                <span className="ml-1 px-2 py-0.5 bg-teal-100 text-teal-800 rounded-full text-xs">
                  {totalItems}
                </span>
              )}
            </button>
            <button
              onClick={() => { setActiveTab('HISTORICAL'); setCurrentPage(1); }}
              className={`px-6 py-3 font-medium text-sm flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === 'HISTORICAL'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <History className="w-4 h-4" />
              Histórico Imutável
            </button>
          </div>

          {/* Filters Area */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 p-4 mb-6 flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por descrição, usuário ou ação..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-200 dark:border-neutral-800"
              />
            </div>

            {activeTab === 'OCCURRENCE' && (
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 text-sm rounded-lg p-2.5 focus:ring-teal-500 focus:border-teal-500"
              >
                <option value="ALL">Todos os Status</option>
                <option value="PENDING">Pendentes</option>
                <option value="RESOLVED">Resolvidos</option>
                <option value="REJECTED">Rejeitados</option>
                <option value="ARCHIVED">Arquivados</option>
              </select>
            )}

            {/* Date Range Picker */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-1.5">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs text-gray-500 font-medium">De:</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                  className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 cursor-pointer"
                />
              </div>
              <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-lg px-3 py-1.5">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                <span className="text-xs text-gray-500 font-medium">Até:</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                  className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 cursor-pointer"
                />
              </div>
              {(startDate || endDate) && (
                <button
                  onClick={handleClearDateFilter}
                  className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 underline"
                >
                  Limpar datas
                </button>
              )}
            </div>

            <Button variant="ghost" onClick={loadLogs} disabled={isLoadingData}>
              {isLoadingData ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Atualizar'}
            </Button>
          </div>

          {/* Logs Table */}
          <div className="bg-white dark:bg-neutral-900 rounded-xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-neutral-800/50 border-b border-gray-200 dark:border-neutral-800">
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Data/Hora</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Ator</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Alvo</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Descrição</th>
                  {activeTab === 'OCCURRENCE' && (
                    <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  )}
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-neutral-800">
                {isLoadingData ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-2" />
                      <p className="text-gray-500">Carregando registros...</p>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <p className="text-gray-500">Nenhum registro encontrado.</p>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800/30 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {format(new Date(log.createdAt), 'dd/MM/yyyy')}
                          </span>
                          <span className="text-xs text-gray-400">
                            {format(new Date(log.createdAt), 'HH:mm:ss')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-2 py-1 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-400 rounded text-xs font-medium">
                          {getTypeLabel(log.type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900 dark:text-white">{log.actor?.name || 'Sistema'}</span>
                          <span className="text-xs text-gray-400">{log.actorRole || '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900 dark:text-white">{log.target?.name || '-'}</span>
                          <span className="text-xs text-gray-400">{log.targetRole || '-'}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate">
                        {log.description}
                      </td>
                      {activeTab === 'OCCURRENCE' && (
                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                          {getStatusBadge(log.status)}
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm text-right whitespace-nowrap">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50"
                          onClick={() => { setSelectedLog(log); setIsDetailsOpen(true); }}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Detalhes
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {!isLoadingData && totalPages > 1 && (
              <div className="px-6 py-4 bg-gray-50 dark:bg-neutral-800/50 border-t border-gray-200 dark:border-neutral-800 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1} a {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} de {totalItems} registros
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                  >
                    Anterior
                  </Button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <Button
                      key={i}
                      variant={currentPage === i + 1 ? 'default' : 'outline'}
                      size="sm"
                      className={currentPage === i + 1 ? 'bg-teal-600' : ''}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </Button>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                  >
                    Próximo
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Details Modal */}
      {isDetailsOpen && selectedLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 w-full max-w-2xl overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-neutral-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-teal-600" />
                Detalhes do Registro
              </h2>
              <button
                onClick={() => setIsDetailsOpen(false)}
                className="text-gray-400 hover:text-gray-500 p-1"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-neutral-800/50 p-4 rounded-xl border border-gray-100 dark:border-neutral-800">
                  <p className="text-xs text-gray-500 uppercase mb-1">Ação</p>
                  <p className="font-semibold text-teal-600">{getTypeLabel(selectedLog.type)}</p>
                </div>
                <div className="bg-gray-50 dark:bg-neutral-800/50 p-4 rounded-xl border border-gray-100 dark:border-neutral-800">
                  <p className="text-xs text-gray-500 uppercase mb-1">Data e Hora</p>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {format(new Date(selectedLog.createdAt), "dd/MM/yyyy 'às' HH:mm:ss")}
                  </p>
                </div>
              </div>

              {/* Participants */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  Participantes envolvidos
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 dark:border-neutral-800 rounded-xl">
                    <p className="text-xs text-gray-500 uppercase mb-2">Quem realizou (Ator)</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedLog.actor?.name || 'Sistema'}</p>
                    <p className="text-xs text-gray-400">{selectedLog.actor?.email || '-'}</p>
                    <Badge variant="outline" className="mt-2">{selectedLog.actorRole || '-'}</Badge>
                  </div>
                  <div className="p-4 border border-gray-200 dark:border-neutral-800 rounded-xl">
                    <p className="text-xs text-gray-500 uppercase mb-2">Afetado (Alvo)</p>
                    <p className="font-medium text-gray-900 dark:text-white">{selectedLog.target?.name || '-'}</p>
                    <p className="text-xs text-gray-400">{selectedLog.target?.email || '-'}</p>
                    <Badge variant="outline" className="mt-2">{selectedLog.targetRole || '-'}</Badge>
                  </div>
                </div>
              </div>

              {/* Description/Message */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  {selectedLog.category === 'OCCURRENCE' ? 'Mensagem da Denúncia' : 'Descrição'}
                </h3>
                <div className="p-4 bg-gray-50 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-xl">
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap italic">
                    {selectedLog.message || selectedLog.description}
                  </p>
                </div>
              </div>

              {/* Metadata */}
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    Informações Adicionais
                  </h3>
                  <div className="bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700 rounded-xl overflow-hidden text-sm">
                    {Object.entries(selectedLog.metadata).map(([key, value]) => (
                      <div key={key} className="flex border-b border-gray-100 dark:border-neutral-700 last:border-0 p-3 hover:bg-gray-50 dark:hover:bg-neutral-700/50 transition-colors">
                        <div className="w-1/3 font-medium text-gray-600 dark:text-gray-400 capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </div>
                        <div className="w-2/3 text-gray-900 dark:text-gray-200 break-words">
                          {typeof value === 'object' && value !== null 
                            ? JSON.stringify(value) 
                            : String(value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Resolution section for occurrences */}
              {selectedLog.category === 'OCCURRENCE' && (
                <div className="space-y-4 pt-4 border-t border-gray-200 dark:border-neutral-800">
                  <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    Tratativa Administrativa
                  </h3>
                  
                  {selectedLog.status !== 'PENDING' && selectedLog.status !== 'IN_ANALYSIS' ? (
                    <div className="p-4 bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-xl space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-green-800 dark:text-green-300">
                          Status: {getTypeLabel(selectedLog.status || '')}
                        </p>
                        <p className="text-xs text-green-600 dark:text-green-400">
                          {selectedLog.resolvedAt ? format(new Date(selectedLog.resolvedAt), 'dd/MM/yyyy HH:mm') : ''}
                        </p>
                      </div>
                      <p className="text-sm text-green-800 dark:text-green-200">
                        <span className="font-medium">Responsável:</span> {selectedLog.resolvedByAdmin?.name || '-'}
                      </p>
                      <p className="text-sm text-green-800 dark:text-green-200 bg-white/50 dark:bg-black/20 p-2 rounded mt-2 italic">
                        "{selectedLog.resolutionNote || 'Nenhuma observação informada.'}"
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Observação Interna</label>
                        <textarea
                          placeholder="Adicione uma nota sobre a decisão tomada..."
                          value={resolutionNote}
                          onChange={(e) => setResolutionNote(e.target.value)}
                          className="w-full h-24 p-3 bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-800 rounded-xl focus:ring-teal-500 focus:border-teal-500 text-sm"
                        />
                      </div>
                      <div className="flex flex-wrap gap-2 pt-2">
                        <Button
                          className="bg-red-600 hover:bg-red-700 text-white gap-2"
                          onClick={() => handleProcess('RESOLVED')}
                          disabled={isProcessing}
                        >
                          <CheckCircle2 className="w-4 h-4" /> Confirmar Suspensão
                        </Button>
                        <Button
                          variant="outline"
                          className="text-gray-700 border-gray-300 hover:bg-gray-100 dark:text-gray-300 dark:border-neutral-700 dark:hover:bg-neutral-800 gap-2"
                          onClick={() => handleProcess('REJECTED')}
                          disabled={isProcessing}
                        >
                          <XCircle className="w-4 h-4" /> Negar Denúncia
                        </Button>
                        <Button
                          variant="ghost"
                          className="text-gray-500 gap-2"
                          onClick={() => handleProcess('ARCHIVED')}
                          disabled={isProcessing}
                        >
                          <Clock className="w-4 h-4" /> Arquivar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 bg-gray-50 dark:bg-neutral-800/50 border-t border-gray-200 dark:border-neutral-800 flex justify-end">
              <Button variant="outline" onClick={() => setIsDetailsOpen(false)}>Fechar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
